import { DateTime } from 'luxon';
import { useExternalParams } from 'shared/hooks';
import { useEffect, useMemo, useRef } from 'react';
import { ProviderFactory } from 'shared/providers/provider-factory';
import { AvailabilityPageParamsContext } from './availability-page-params-provider.types';

const initialValues = {
  search: { initialValue: undefined, type: 'string' },
  date: { initialValue: undefined, type: 'date' },
  flexPoolIds: { initialValue: undefined, type: 'string_array' },
  companyId: { initialValue: undefined, type: 'string' },
  companyName: { initialValue: undefined, type: 'string' },
  allFlexpools: { initialValue: undefined, type: 'boolean' },
} as const;

const [AvailabilityContextProviderComponent, useAvailabilityPageParamsProvider] =
  ProviderFactory<AvailabilityPageParamsContext>(
    'AvailabilityPageParamsContext can only be accessed within AvailabilityPageParamsProvider'
  );

type AvailabilityPageParamsProviderProps = {
  children: React.ReactNode;
};

export const AvailabilityPageParamsProvider = ({
  children,
}: AvailabilityPageParamsProviderProps) => {
  const [params, setParams] = useExternalParams(initialValues);
  const savedParamsRef = useRef(params);

  useEffect(() => {
    savedParamsRef.current = {
      ...savedParamsRef.current,
      search: params.search,
      date: params.date,
      flexPoolIds: params.flexPoolIds,
    };
  }, [params.search, params.date, params.flexPoolIds]);

  const filters = useMemo(
    () => ({
      search: params.search || undefined,
      date: params.date || undefined,
      flexPoolIds: params.flexPoolIds || undefined,
      companyId: params.companyId || undefined,
      companyName: params.companyName || undefined,
      allFlexpools: params.allFlexpools || undefined,
    }),
    [params]
  );

  const selectedFiltersCount = useMemo(
    () =>
      Object.values(filters).filter(
        (value) => value !== null && value !== undefined && value !== '' && !isDateTime(value)
      ).length,
    [filters]
  );

  return (
    <AvailabilityContextProviderComponent
      value={{
        filters,
        savedParamsRef,
        setFilters: setParams,
        selectedFiltersCount,
      }}
    >
      {children}
    </AvailabilityContextProviderComponent>
  );
};

export const useAvailabilityPageParamsContext = () => {
  return useAvailabilityPageParamsProvider();
};

const isDateTime = (filterValue: any): filterValue is DateTime => filterValue instanceof DateTime;
